// Project → Files tab. Staff-uploaded artwork / inspiration / proofs /
// finals attached to a Project container. Mirrors the portal Brand
// Assets gallery but project-scoped + staff audience.
//
// Endpoints (all on the projects router):
//   GET    /projects/:id/files               — paginated gallery
//   POST   /projects/:id/files               — presign upload + register
//   DELETE /projects/:id/files/:fileId       — soft-delete (R2 object kept)
//   GET    /projects/:id/files/:fileId/download — short-lived presigned R2 URL
import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useOutletContext } from "react-router-dom";
import { Upload, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/Button";
import { timeAgo } from "@/lib/utils";
import type { ProjectDetailResponse } from "@/pages/ProjectDetailPage";

type ProjectFileTag = "artwork" | "inspiration" | "proof" | "final" | "other";

const TAG_LABEL: Record<ProjectFileTag, string> = {
  artwork: "Artwork",
  inspiration: "Inspiration",
  proof: "Proof",
  final: "Final",
  other: "Other",
};

const TAG_ORDER: ProjectFileTag[] = [
  "artwork",
  "inspiration",
  "proof",
  "final",
  "other",
];

const TAG_PILL_CLASS: Record<ProjectFileTag, string> = {
  artwork: "bg-peach-200 text-brown-500",
  inspiration: "bg-peach-50 text-brown-500",
  proof: "bg-cream-200 text-brown-500",
  final: "bg-peach-400 text-white",
  other: "bg-cream-100 text-brown-400",
};

interface ProjectFileRow {
  id: string;
  tag: ProjectFileTag;
  fileId: string;
  originalFilename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  uploadedByUserId: string;
  uploadedByName: string | null;
}

interface ProjectFilesListResponse {
  rows: ProjectFileRow[];
  nextCursor: { uploadedAt: string; id: string } | null;
}

interface CreateResponse {
  project_file_id: string;
  file_id: string;
  upload_url: string;
  live: boolean;
}

interface DownloadResponse {
  url: string;
  expires_at: string;
  mime_type: string;
  original_filename: string;
}

function isImageMime(mime: string): boolean {
  return mime.startsWith("image/");
}

function formatBytes(b: number): string {
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / (1024 * 1024)).toFixed(1)} MB`;
}

// ─── Drag-and-drop helpers ────────────────────────────────────────────
// Pure + self-contained (no imports) so the offline QA gate can EXECUTE
// them, not just source-lint. The upload card wires these into its
// drag handlers; dropped files flow through the SAME upload mutation the
// Choose-file input uses.

/**
 * Classes for the upload card. When a real file drag is hovering the
 * card we swap in a peach highlight (border + fill + ring) so the drop
 * target is unmistakable. Brand palette only — no gray, no black.
 * The active/idle branch is what the gate executes: false && sabotage
 * collapses both branches to idle and the gate goes red by name.
 */
export function dropZoneClasses(dragActive: boolean): string {
  // `transition` (not transition-colors) so the ring-2 box-shadow animates
  // in step with the border/bg instead of hard-cutting against them.
  const base =
    "mb-4 flex flex-wrap items-end justify-between gap-3 rounded-lg border p-4 transition";
  const idle = "border-peach-300 bg-peach-50";
  const active = "border-peach-500 bg-peach-100 ring-2 ring-peach-300";
  return `${base} ${dragActive ? active : idle}`;
}

/**
 * Busy-state treatment for the CARD while an upload is running — dimmed
 * only, NOT pointer-events-none. Keeping pointer events on the card means
 * it still receives dragenter/over/drop while busy, so shouldHighlight
 * can suppress the highlight, the "busy" subtext is reachable, and the
 * card's own handleDrop can reject an in-flight drop with a message.
 */
export function busyCardClasses(uploadPending: boolean): string {
  return uploadPending ? "opacity-60 cursor-not-allowed" : "";
}

/**
 * Non-interactive treatment scoped to just the Tag select + Choose
 * button. pointer-events-none lives HERE, not on the card — so the
 * controls are disabled during an upload without deafening the card to
 * drags (which would make the busy state unreachable).
 */
export function busyControlsClasses(uploadPending: boolean): string {
  return uploadPending ? "pointer-events-none" : "";
}

/**
 * Real files carried by a drop / DataTransfer. Drags that carry no
 * files (text selections, links, dragged page elements) yield an empty
 * list and are ignored by the drop handler. Multiple files pass
 * through intact for sequential upload. A dragged folder or zero-byte
 * phantom surfaces as a 0-size entry — filtered out here so we never
 * mint a garbage empty project file.
 */
export function extractDropFiles(dt: DataTransfer | null): File[] {
  if (!dt || !dt.files || dt.files.length === 0) return [];
  return Array.from(dt.files).filter((f) => f.size > 0);
}

/** True when a drag event is carrying file content worth highlighting. */
export function dragHasFiles(dt: DataTransfer | null): boolean {
  if (!dt || !dt.types) return false;
  return Array.from(dt.types).includes("Files");
}

/**
 * The peach highlight fires only when a real file drag is over the card
 * AND no upload is currently running — an in-flight drag must NOT invite
 * a drop the handler will silently eat. The `!uploadPending` clause is
 * what the gate executes: drop it (or false && it) and shouldHighlight
 * stays true during an upload, going red by name.
 */
export function shouldHighlight(dragOver: boolean, uploadPending: boolean): boolean {
  return dragOver && !uploadPending;
}

/**
 * Which subtext the drop card shows. All three strings are rendered
 * stacked in one grid cell (only the active one visible) so the card's
 * footprint never reflows mid-drag; this returns the active kind. During
 * an in-flight upload a hovered drag says so plainly instead of the
 * misleading "Release to upload."
 */
export function dropSubtextKind(
  dragOver: boolean,
  uploadPending: boolean,
): "idle" | "release" | "busy" {
  if (dragOver && uploadPending) return "busy";
  if (dragOver) return "release";
  return "idle";
}

/**
 * Final batch summary after a (possibly multi-file) upload settles.
 * Accumulated per-file failures survive even when later files in the
 * same batch succeed — a success can never silently erase an earlier
 * failure. One line per failure plus an "N of M uploaded" tally. A
 * fully-clean batch returns null, clearing any prior error.
 */
export function summarizeUploadFailures(
  failures: { name: string; reason: string }[],
  total: number,
): string | null {
  if (failures.length === 0) return null;
  const lines = failures.map((f) => `${f.name} failed: ${f.reason}`);
  lines.push(
    `${total - failures.length} of ${total} file${total === 1 ? "" : "s"} uploaded.`,
  );
  return lines.join("\n");
}

/** Shown when a drop is rejected because an upload is already running. */
export const UPLOAD_IN_FLIGHT_MSG =
  "An upload is already in progress — wait for it to finish, then try again.";

export function ProjectFilesTab() {
  const data = useOutletContext<ProjectDetailResponse>();
  const projectId = data.project.id;
  const queryClient = useQueryClient();

  const [tagFilter, setTagFilter] = useState<ProjectFileTag | null>(null);
  const [pendingTag, setPendingTag] = useState<ProjectFileTag>("artwork");
  const [error, setError] = useState<string | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  // Depth counter: dragenter/dragleave fire per child element, so a naive
  // boolean strobes the highlight as the pointer crosses inner nodes.
  // Counting enters vs leaves keeps the state steady until the drag truly
  // exits the card.
  const dragDepth = useRef(0);

  const queryKey = ["project", projectId, "files", tagFilter] as const;
  const { data: list, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => {
      const params = new URLSearchParams();
      if (tagFilter) params.set("tag", tagFilter);
      params.set("limit", "100");
      return api<ProjectFilesListResponse>(
        `/projects/${projectId}/files?${params.toString()}`,
      );
    },
  });

  const uploadMutation = useMutation({
    mutationFn: async (args: { file: File; tag: ProjectFileTag }) => {
      const presign = await api<CreateResponse>(
        `/projects/${projectId}/files`,
        {
          method: "POST",
          body: JSON.stringify({
            filename: args.file.name,
            mime_type: args.file.type || "application/octet-stream",
            size_bytes: args.file.size,
            tag: args.tag,
          }),
        },
      );
      if (presign.live) {
        const put = await fetch(presign.upload_url, {
          method: "PUT",
          headers: {
            "Content-Type": args.file.type || "application/octet-stream",
          },
          body: args.file,
        });
        if (!put.ok) {
          throw new Error(`R2 upload failed: ${put.status} ${put.statusText}`);
        }
      }
      return presign;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "files"],
      });
      // Error state is owned by uploadFiles' own loop (F2) — the mutation
      // must NOT clear/set it per-file, or a later success in a multi-file
      // batch would silently erase an earlier file's failure.
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      await api(`/projects/${projectId}/files/${id}`, { method: "DELETE" });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ["project", projectId, "files"],
      });
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : "Delete failed.");
    },
  });

  // Single shared upload path for BOTH the Choose-file input and drag-drop.
  // One presign+PUT per file via the same mutation and the same pendingTag.
  // Multiple dropped files run sequentially (chained), so we never fan out
  // parallel presigns — same one-at-a-time behavior as picking files in a
  // row through the input.
  const uploadFiles = useCallback(
    (files: File[]) => {
      if (files.length === 0) return;
      setError(null);
      // Accumulate failures IN THIS LOOP (not via the mutation's onError)
      // so a fail-then-succeed sequence in a multi-file drop keeps every
      // failure. The final summary is set once, after the chain settles,
      // where a later success can't overwrite it.
      const failures: { name: string; reason: string }[] = [];
      void files
        .reduce<Promise<unknown>>(
          (chain, file) =>
            chain.then(() =>
              uploadMutation
                .mutateAsync({ file, tag: pendingTag })
                .catch((err) => {
                  failures.push({
                    name: file.name,
                    reason: err instanceof Error ? err.message : "Upload failed.",
                  });
                }),
            ),
          Promise.resolve(),
        )
        .then(() => {
          setError(summarizeUploadFailures(failures, files.length));
        });
    },
    [pendingTag, uploadMutation],
  );

  const onFileSelected = useCallback(
    (file: File) => {
      uploadFiles([file]);
    },
    [uploadFiles],
  );

  // Latest-value refs so the window-level backstop (mounted once for the
  // tab's lifetime) always reads the current upload fn + pending state
  // instead of stale mount-time closures.
  const uploadFilesRef = useRef(uploadFiles);
  uploadFilesRef.current = uploadFiles;
  const isPendingRef = useRef(uploadMutation.isPending);
  isPendingRef.current = uploadMutation.isPending;

  // F3 — window-level navigation backstop for the tab's lifetime. Without
  // it, a file dropped anywhere OUTSIDE the card (grid, filters, header)
  // hits the browser default and NAVIGATES AWAY from the app. We prevent
  // that everywhere, and forward drops that land inside the Files tab
  // section into the same upload path (with the same in-flight honesty).
  // Drops on the card itself are handled by its React onDrop — skipped
  // here so they aren't processed twice.
  useEffect(() => {
    function onWindowDragOver(e: DragEvent): void {
      if (!dragHasFiles(e.dataTransfer)) return;
      e.preventDefault();
    }
    function onWindowDrop(e: DragEvent): void {
      if (!dragHasFiles(e.dataTransfer)) return;
      e.preventDefault(); // no navigation, anywhere on the tab
      const target = e.target as Node | null;
      if (cardRef.current && target && cardRef.current.contains(target)) {
        return; // card's own onDrop already handled it
      }
      if (sectionRef.current && target && sectionRef.current.contains(target)) {
        if (isPendingRef.current) {
          setError(UPLOAD_IN_FLIGHT_MSG);
          return;
        }
        const files = extractDropFiles(e.dataTransfer);
        if (files.length > 0) uploadFilesRef.current(files);
      }
    }
    window.addEventListener("dragover", onWindowDragOver);
    window.addEventListener("drop", onWindowDrop);
    return () => {
      window.removeEventListener("dragover", onWindowDragOver);
      window.removeEventListener("drop", onWindowDrop);
    };
    // Refs keep this current; the listener lives for the tab's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function triggerFilePicker(): void {
    fileInputRef.current?.click();
  }
  function onInputChange(e: React.ChangeEvent<HTMLInputElement>): void {
    const f = e.target.files?.[0];
    if (f) onFileSelected(f);
    e.target.value = "";
  }

  // ─── Drag-and-drop handlers on the upload card ──────────────────────
  function handleDragEnter(e: React.DragEvent<HTMLDivElement>): void {
    if (!dragHasFiles(e.dataTransfer)) return; // ignore non-file drags
    e.preventDefault();
    dragDepth.current += 1;
    setDragActive(true);
  }
  function handleDragOver(e: React.DragEvent<HTMLDivElement>): void {
    if (!dragHasFiles(e.dataTransfer)) return;
    // preventDefault is mandatory here — without it the browser treats the
    // card as a non-drop target and the drop event never fires.
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }
  function handleDragLeave(e: React.DragEvent<HTMLDivElement>): void {
    if (!dragHasFiles(e.dataTransfer)) return;
    e.preventDefault();
    dragDepth.current -= 1;
    if (dragDepth.current <= 0) {
      dragDepth.current = 0;
      setDragActive(false);
    }
  }
  function handleDrop(e: React.DragEvent<HTMLDivElement>): void {
    e.preventDefault();
    dragDepth.current = 0;
    setDragActive(false);
    // Reject a drop while an upload is already running — mirrors the
    // Choose-file button being disabled — and say so visibly (F1) rather
    // than silently eating the drop.
    if (uploadMutation.isPending) {
      setError(UPLOAD_IN_FLIGHT_MSG);
      return;
    }
    const files = extractDropFiles(e.dataTransfer);
    if (files.length === 0) return; // non-file drop: nothing to do
    uploadFiles(files);
  }

  const rows = list?.rows ?? [];
  const subtextKind = dropSubtextKind(dragActive, uploadMutation.isPending);

  return (
    <section ref={sectionRef}>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="text-lg font-semibold tracking-[-0.4px] text-ink">
            Files{" "}
            <span className="font-normal text-muted">({rows.length})</span>
          </h2>
          <p className="mt-0.5 text-xs text-muted">
            Team files for this project. Uploading a proof here doesn't mean the client approved it.
          </p>
        </div>
      </div>

      {/* Upload row — also the drag-and-drop target */}
      <div
        ref={cardRef}
        className={cn(
          dropZoneClasses(shouldHighlight(dragActive, uploadMutation.isPending)),
          busyCardClasses(uploadMutation.isPending),
        )}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        aria-label="Drop files here to upload to this project"
      >
        <div className="flex-1">
          <h3 className="text-sm font-medium text-ink">
            Drop a file on this project
          </h3>
          {/* All three subtexts share one grid cell; only the active one
              is visible so the card never reflows mid-drag (S4). */}
          <p className="mt-1 grid text-xs text-brown-400">
            <span
              className={cn(
                "col-start-1 row-start-1",
                subtextKind !== "idle" && "invisible",
              )}
            >
              Logos, artwork, inspiration, proofs. Up to 50&nbsp;MB.
            </span>
            <span
              className={cn(
                "col-start-1 row-start-1",
                subtextKind !== "release" && "invisible",
              )}
            >
              Release to upload.
            </span>
            <span
              className={cn(
                "col-start-1 row-start-1",
                subtextKind !== "busy" && "invisible",
              )}
            >
              Upload in progress…
            </span>
          </p>
        </div>
        <div
          className={cn(
            "flex flex-wrap items-end gap-3",
            busyControlsClasses(uploadMutation.isPending),
          )}
        >
          <label className="block">
            <span className="mb-1 block text-xs text-muted">
              Tag
            </span>
            <select
              value={pendingTag}
              onChange={(e) => setPendingTag(e.target.value as ProjectFileTag)}
              className="h-10 rounded-md border border-rule bg-white px-3 text-sm text-ink"
            >
              {TAG_ORDER.map((t) => (
                <option key={t} value={t}>
                  {TAG_LABEL[t]}
                </option>
              ))}
            </select>
          </label>
          <input
            ref={fileInputRef}
            type="file"
            onChange={onInputChange}
            className="hidden"
          />
          <Button
            type="button"
            variant="primary"
            onClick={triggerFilePicker}
            disabled={uploadMutation.isPending}
          >
            <Upload className="h-4 w-4" />
            {uploadMutation.isPending ? "Uploading…" : "Choose file"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="mb-4 whitespace-pre-line rounded-md border border-rust-200 bg-rust-50 px-4 py-3 text-sm text-rust-500">
          {error}
        </div>
      )}

      {/* Filter chips */}
      <div
        className="mb-4 flex flex-wrap gap-x-5 border-b border-rule"
        role="tablist"
        aria-label="Filter files by tag"
      >
        <FilterChip
          label="All"
          active={tagFilter === null}
          onClick={() => setTagFilter(null)}
        />
        {TAG_ORDER.map((t) => (
          <FilterChip
            key={t}
            label={TAG_LABEL[t]}
            active={tagFilter === t}
            onClick={() => setTagFilter(tagFilter === t ? null : t)}
          />
        ))}
      </div>

      {/* Grid */}
      {isLoading ? (
        <div className="h-40 animate-pulse rounded-md bg-white" aria-label="Loading files" />
      ) : isError ? (
        <div className="rounded-md border border-rule bg-white p-5 text-sm text-risk" role="alert">
          We couldn't load files. Nothing has changed — refresh to try again.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-md border border-rule bg-white px-5 py-12 text-center text-sm text-muted">
          {tagFilter
            ? `No ${TAG_LABEL[tagFilter].toLowerCase()} files yet.`
            : "No files yet. Drop the first one above — client logos, mockups, mood boards."}
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {rows.map((f) => (
            <FileTile
              key={f.id}
              projectId={projectId}
              file={f}
              onDelete={() => {
                if (
                  window.confirm(`Delete "${f.originalFilename}"? This can't be undone here.`)
                ) {
                  deleteMutation.mutate(f.id);
                }
              }}
              deleting={deleteMutation.isPending}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        "-mb-px min-h-[40px] border-b-2 px-0.5 text-sm transition-colors",
        active
          ? "border-ink font-medium text-ink"
          : "border-transparent text-muted hover:text-ink",
      )}
    >
      {label}
    </button>
  );
}

interface FileTileProps {
  projectId: string;
  file: ProjectFileRow;
  onDelete: () => void;
  deleting: boolean;
}

function FileTile({ projectId, file, onDelete, deleting }: FileTileProps) {
  const isImage = isImageMime(file.mimeType);
  const { data: download } = useQuery({
    queryKey: ["project", projectId, "file-download", file.fileId],
    queryFn: () =>
      api<DownloadResponse>(
        `/projects/${projectId}/files/${file.fileId}/download`,
      ),
    enabled: isImage,
    staleTime: 8 * 60 * 1000,
  });

  return (
    <div className="overflow-hidden rounded-lg border border-brown-100 bg-white">
      <div className="flex h-40 items-center justify-center bg-cream-100">
        {isImage && download?.url ? (
          <img
            src={download.url}
            alt={file.originalFilename}
            className="h-full w-full object-contain"
          />
        ) : (
          <div className="px-4 text-center">
            <div className="font-display text-[11px] font-bold uppercase tracking-wide text-brown-300">
              {file.mimeType.split("/")[1] ?? "File"}
            </div>
            <div className="mt-2 text-xs text-brown-400">
              {formatBytes(file.sizeBytes)}
            </div>
          </div>
        )}
      </div>
      <div className="p-3">
        <span
          className={cn(
            "inline-flex items-center rounded px-2 py-1 text-[11px] font-medium",
            TAG_PILL_CLASS[file.tag],
          )}
        >
          {TAG_LABEL[file.tag]}
        </span>
        <div
          className="mt-2 truncate text-sm font-semibold text-brown-500"
          title={file.originalFilename}
        >
          {file.originalFilename}
        </div>
        <div className="mt-0.5 text-xs text-brown-300">
          {file.uploadedByName ?? "—"} · {timeAgo(file.uploadedAt)} ·{" "}
          {formatBytes(file.sizeBytes)}
        </div>
        <div className="mt-3 flex items-center justify-between text-xs">
          {download?.url ? (
            <a
              href={download.url}
              target="_blank"
              rel="noopener noreferrer"
              className="font-semibold text-ink underline-offset-2 hover:underline"
            >
              Download
            </a>
          ) : (
            <DownloadLazyButton projectId={projectId} fileId={file.fileId} />
          )}
          <button
            type="button"
            onClick={onDelete}
            disabled={deleting}
            aria-label="Delete file"
            className="inline-flex items-center gap-1 font-semibold text-brown-400 underline-offset-2 hover:text-rust-500 hover:underline disabled:opacity-50"
          >
            <Trash2 className="h-3.5 w-3.5" />
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function DownloadLazyButton({
  projectId,
  fileId,
}: {
  projectId: string;
  fileId: string;
}) {
  const [loading, setLoading] = useState(false);
  async function onClick(): Promise<void> {
    setLoading(true);
    try {
      const res = await api<DownloadResponse>(
        `/projects/${projectId}/files/${fileId}/download`,
      );
      window.open(res.url, "_blank", "noopener,noreferrer");
    } finally {
      setLoading(false);
    }
  }
  return (
    <button
      type="button"
      onClick={() => void onClick()}
      disabled={loading}
      className="font-semibold text-ink underline-offset-2 hover:underline disabled:opacity-50"
    >
      {loading ? "Opening…" : "Download"}
    </button>
  );
}
